import { useState, type MouseEvent } from 'react'
import { Link } from 'react-router-dom'

import type { FevDeCartera, FilaCartera } from '@/lib/supabase/tipos-bd'
import { fecha, pesos } from '@/shared/utilidades/formato'

import { AnotarPago } from './AnotarPago'
import { DocumentosDeCuenta } from './DocumentosDeCuenta'
import { debePasarAFev, edadDe, estadoDe, PASOS, pasoDe } from './edades'
import { YaEnUnaFev } from './YaEnUnaFev'
import { PasarServiciosAFev } from './PasarServiciosAFev'
import { HojaDeLaFactura } from './HojaDeLaFactura'
import { hoy } from '@/modules/tablero/semana'
import { PagosDeCuenta } from './PagosDeCuenta'
import estilos from './PaginaCartera.module.css'

/**
 * UNA CUENTA POR COBRAR, COMO TARJETA.
 *
 * Era un renglón de tabla —OS, fecha, valor, pagado, saldo, botón— y Johan
 * pidió que fuera «más didáctico, versátil». Cada tarjeta cuenta la historia
 * de la cuenta sin que haya que saber de cartera:
 *   · de quién es, qué servicio fue y con qué facturas (FEV) se cobra;
 *   · cuánto se ha pagado, con una barra, y cuánto falta, en grande;
 *   · qué tan vieja es, en el color de su edad;
 *   · en qué paso va —Finalizado, Facturada, Abonada, Pagada—, marcado.
 *
 * «SOLO PASAN A CARTERA FEV» (0251): sin factura no hay qué cobrar. Esa
 * tarjeta lo dice con todas las letras —cuántas cotizaciones esperan pasar a
 * FEV— y no deja anotar pagos.
 *
 * SE EXPANDE (Johan: «¿por qué no me deja expandir la OS?… para ver todas
 * las FEV»). Tocando la tarjeta —o «Facturas, cotizaciones y pagos»— se abre
 * a todo el ancho: las FEV de la OS (cada una abre su hoja), las
 * cotizaciones y los pagos anotados. El número de la OS sigue llevando a la
 * orden.
 */
export type Panel = 'pagar' | 'detalle' | null

/** Lo que no debe abrir la tarjeta al tocarlo: los controles de adentro. */
const INTERACTIVO = 'a, button, input, select, textarea, label, form'

export function TarjetaDeCuenta({
  cuenta: c,
  panel,
  onPanel,
  sinCliente = false,
}: {
  cuenta: FilaCartera
  panel: Panel
  onPanel: (p: Panel) => void
  /** Agrupadas por cliente, el nombre ya está en el encabezado del grupo. */
  sinCliente?: boolean
}) {
  const [viendo, setViendo] = useState<FevDeCartera | null>(null)
  const valor = Number(c.valor)
  const pagado = Number(c.pagado)
  const saldo = Number(c.saldo)
  const edad = edadDe(c.dias)
  const estado = estadoDe(c)
  const paso = pasoDe(c)
  const avance = valor > 0 ? Math.min(100, (pagado / valor) * 100) : 0
  const fev = c.fev ?? []
  const cot = c.cot ?? []
  // Sin IVA (0264): no pasa a FEV y cuenta como facturada, para seguirle el
  // cobro como a una FEV. Las demás COT sí se deben pasar a FEV.
  const sinIva = cot.filter((k) => k.sin_iva && !k.remision)
  // 0296: decidida como remisión: no pasa a FEV, se cobra como está.
  const remisiones = cot.filter((k) => k.remision)
  const porPasar = cot.filter((k) => !k.sin_iva && !k.remision)
  const alerta = debePasarAFev(c)

  return (
    <article
      className={`${estilos.cuenta} ${panel ? estilos.cuentaAbierta : ''} ${
        estado === 'sin_documentos' ? estilos.cuentaSinFev : ''
      } ${alerta ? estilos.cuentaDebeFev : ''}`}
      onClick={(e: MouseEvent<HTMLElement>) => {
        // La hoja de la factura vive en un portal: sus clics también suben
        // por aquí en React y no deben abrir ni cerrar la tarjeta.
        if (!e.currentTarget.contains(e.target as Node)) return
        if ((e.target as HTMLElement).closest(INTERACTIVO)) return
        onPanel(panel === 'detalle' ? null : 'detalle')
      }}
    >
      <header className={estilos.cuentaTope}>
        <div className={estilos.cuentaQuien}>
          <Link to={`/ordenes/${c.orden_servicio_id}`} className={estilos.numero}>
            {c.numero}
          </Link>
          {!sinCliente && (
            <p className={estilos.cliente} title={c.cliente ?? undefined}>
              {c.cliente ?? 'Sin cliente'}
            </p>
          )}
          {c.servicio && <p className={estilos.servicio}>{c.servicio}</p>}
        </div>
        <span
          className={`${estilos.edad} ${
            estado === 'pagada'
              ? estilos.edadPagada
              : estado === 'sin_documentos'
              ? estilos.edadSinFev
              : estilos[edad.clave]
          }`}
          title={`Finalizado el ${fecha(c.finalizado_en)}`}
        >
          {estado === 'pagada'
            ? 'al día'
            : estado === 'sin_documentos'
            ? 'sin documentos'
            : `hace ${c.dias} ${c.dias === 1 ? 'día' : 'días'}`}
        </span>
      </header>

      {/* Las FEV con que se cobra, de un vistazo: el número de cada una. */}
      {(fev.length > 0 || cot.length > 0) && (
        <p className={estilos.fevChips}>
          {/* Botones, no etiquetas (Johan: «no parecen botones»): la FEV abre
              su factura y la COT despliega la cuenta, donde se pasa a FEV. */}
          {fev.map((f) => (
            <button
              key={f.wo_id_asiento}
              type="button"
              className={estilos.fevChip}
              onClick={() => setViendo(f)}
              title="Ver la factura"
            >
              {f.numero}
              {(f.contado || Number(f.saldo) <= 0) && ' · pagada'} ›
            </button>
          ))}
          {sinIva.map((k) => (
            <button
              key={k.cotizacion_id}
              type="button"
              className={estilos.fevChip}
              onClick={() => onPanel('detalle')}
              title="Sin IVA: no lleva FEV, cuenta como facturada"
            >
              {k.numero} · sin IVA ›
            </button>
          ))}
          {remisiones.map((k) => (
            <button
              key={k.cotizacion_id}
              type="button"
              className={estilos.fevChip}
              onClick={() => onPanel('detalle')}
              title="Se decidió como remisión: no pasa a FEV"
            >
              {k.numero} · remisión ›
            </button>
          ))}
          {porPasar.map((k) => (
            <button
              key={k.cotizacion_id}
              type="button"
              className={estilos.cotChip}
              onClick={() => onPanel('detalle')}
              title="Todavía es COT"
            >
              COT · {k.numero} ›
            </button>
          ))}
        </p>
      )}

      {/* LA ALERTA (0254): hay COT en la cuenta y se deben pasar a FEV. La
          cuenta se cobra igual; lo que no puede pasar es que se olvide. */}
      {alerta && (
        <div className={estilos.debeFev} role="alert">
          <p className={estilos.debeFevTitulo}>
            <span aria-hidden="true">!</span>
            {c.cot_sin_fev === 1
              ? '1 COT por pasar a FEV'
              : `${c.cot_sin_fev} COT por pasar a FEV`}{' '}
            · {pesos(Number(c.valor_cot))}
          </p>
          <p className={estilos.debeFevTexto}>
            {cot.length > 0 && cot.every((k) => k.fev_pedida)
              ? 'Bodega ya la pidió; falta que World Office la convierta en factura.'
              : 'Se debe pasar a FEV en Bodega → Alistamientos. Si ya se facturó en World Office, márquela abajo.'}
          </p>
        </div>
      )}

      {estado === 'sin_documentos' ? (
        <div className={estilos.sinFev}>
          <p className={estilos.sinFevTitulo}>Sin FEV ni COT</p>
          <p className={estilos.sinFevTexto}>
            El servicio está finalizado pero no tiene nada que cobrar todavía.
          </p>
        </div>
      ) : (
        // Lo que falta, en grande: es lo que se viene a buscar aquí.
        <div className={estilos.cuentaPlata}>
          <p className={estilos.falta}>
            {saldo > 0 ? (
              <>
                <span>Falta</span> {pesos(saldo)}
              </>
            ) : (
              <span className={estilos.pagadaTexto}>Pagada completa</span>
            )}
          </p>
          <div
            className={estilos.avance}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(avance)}
            aria-label="Cuánto se ha pagado"
          >
            <span style={{ width: `${avance}%` }} />
          </div>
          <p className={estilos.avanceTexto}>
            <b>{pesos(pagado)}</b> de {pesos(valor)} pagado
            {c.pagos > 0 && (
              <>
                {' '}
                · {c.pagos} {c.pagos === 1 ? 'pago' : 'pagos'}
              </>
            )}
            {alerta && Number(c.valor_fev) > 0 && (
              <>
                {' '}
                · FEV {pesos(Number(c.valor_fev))} + COT {pesos(Number(c.valor_cot))}
              </>
            )}
          </p>
        </div>
      )}

      {/* La vida de la cuenta: de dónde viene y qué le falta. */}
      <ol className={estilos.pasos} aria-label="En qué va la cuenta">
        {PASOS.map((nombre, i) => (
          <li
            key={nombre}
            className={i < paso ? estilos.pasoHecho : i === paso ? estilos.pasoActual : undefined}
            aria-current={i === paso ? 'step' : undefined}
          >
            <span className={estilos.pasoPunto} aria-hidden="true" />
            <span className={estilos.pasoNombre}>{nombre}</span>
          </li>
        ))}
      </ol>

      <footer className={estilos.cuentaAcciones}>
        <button
          type="button"
          className={`${estilos.expandir} ${panel === 'detalle' ? estilos.expandirVivo : ''}`}
          onClick={() => onPanel(panel === 'detalle' ? null : 'detalle')}
          aria-expanded={panel === 'detalle'}
        >
          <span className={estilos.chevron} aria-hidden="true">
            ›
          </span>
          {[
            fev.length > 0 && `${fev.length} FEV`,
            sinIva.length > 0 && `${sinIva.length} sin IVA`,
            porPasar.length > 0 && `${porPasar.length} COT`,
            'pagos',
          ]
            .filter(Boolean)
            .join(', ')}
        </button>
        {estado !== 'pagada' && (
          <button
            type="button"
            className={panel === 'pagar' ? estilos.secundarioVivo : estilos.principal}
            onClick={() => onPanel(panel === 'pagar' ? null : 'pagar')}
            aria-expanded={panel === 'pagar'}
            // Sin nada que cobrar no hay saldo: la base tampoco lo dejaría.
            disabled={estado === 'sin_documentos'}
          >
            {panel === 'pagar' ? 'Cerrar' : 'Anotar pago'}
          </button>
        )}
      </footer>

      {panel === 'pagar' && <AnotarPago cuenta={c} onListo={() => onPanel(null)} />}
      {panel === 'detalle' && (
        <div className={estilos.detalle}>
          <section>
            <p className={estilos.detalleTitulo}>
              {sinIva.length > 0 ? 'Facturado (FEV y sin IVA)' : 'Facturas de venta (FEV)'}
            </p>
            {fev.length === 0 && sinIva.length === 0 ? (
              <p className={estilos.pagosVacio}>Esta OS todavía no tiene FEV.</p>
            ) : (
              <ul className={estilos.documentos}>
                {sinIva.map((k) => (
                  <li key={k.cotizacion_id} className={estilos.documento}>
                    <span className={`${estilos.docClase} ${estilos.docFactura}`}>Sin IVA</span>
                    <span className={estilos.docNumero}>{k.numero}</span>
                    <span className={estilos.docEstado}>
                      No lleva IVA: no pasa a FEV, se cobra con la cotización
                    </span>
                    <span className={estilos.docValor}>{pesos(Number(k.valor))}</span>
                  </li>
                ))}
                {fev.map((f) => (
                  <li key={f.wo_id_asiento}>
                    <button
                      type="button"
                      className={`${estilos.documento} ${estilos.documentoBoton}`}
                      onClick={() => setViendo(f)}
                      title="Ver la factura"
                    >
                      <span className={`${estilos.docClase} ${estilos.docFactura}`}>Factura</span>
                      <span className={estilos.docNumero}>{f.numero}</span>
                      <span className={estilos.docEstado}>
                        {f.fecha ? fecha(f.fecha) : 'sin fecha'}
                        {f.de_la_cotizacion && <> · de la cotización {f.de_la_cotizacion}</>}
                        {f.cufe ? (
                          <> · CUFE {f.cufe.slice(0, 8)}…</>
                        ) : (
                          <> · pendiente de presentar a la DIAN</>
                        )}
                        {/* Lo que debe según World Office (0293). */}
                        <span className={estilos.docSaldo}>
                          {f.contado
                            ? 'De contado: pagada al facturar'
                            : Number(f.saldo) <= 0
                            ? 'Pagada en World Office'
                            : `Debe ${pesos(Number(f.saldo))}${
                                f.vence
                                  ? ` · ${f.vence < hoy() ? 'venció' : 'vence'} ${fecha(f.vence)}`
                                  : ''
                              }`}
                        </span>
                      </span>
                      <span className={estilos.docValor}>
                        {pesos(Number(f.total))} <span className={estilos.docAbrir}>Ver ›</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {porPasar.length > 0 && (
              <>
                <p
                  className={`${estilos.detalleTitulo} ${estilos.detalleTituloSegundo} ${estilos.detalleTituloRojo}`}
                >
                  Por pasar a FEV
                </p>
                <ul className={estilos.documentos}>
                  {porPasar.map((k) => (
                    <li
                      key={k.cotizacion_id}
                      className={`${estilos.documento} ${estilos.documentoCot}`}
                    >
                      <span className={`${estilos.docClase} ${estilos.docCot}`}>COT</span>
                      <span className={estilos.docNumero}>{k.numero}</span>
                      <span className={estilos.docEstado}>
                        {k.fev_pedida
                          ? 'pedida a FEV, esperando World Office'
                          : k.estado === 'aprobada'
                          ? 'aceptada · se debe pasar a FEV'
                          : 'sin aceptar · se debe pasar a FEV'}
                      </span>
                      <span className={estilos.docValor}>{pesos(Number(k.valor))}</span>
                      <span className={estilos.docAccion}>
                        {/* 0301: solo servicios, aceptada: se pasa a FEV aquí. */}
                        {k.solo_servicios && k.estado === 'aprobada' && !k.fev_pedida && (
                          <PasarServiciosAFev cotizacionId={k.cotizacion_id} />
                        )}
                        <YaEnUnaFev cotizacionId={k.cotizacion_id} />
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <p className={`${estilos.detalleTitulo} ${estilos.detalleTituloSegundo}`}>
              Cotizaciones de la OS
            </p>
            <DocumentosDeCuenta ordenServicioId={c.orden_servicio_id} soloCotizaciones />
          </section>
          <section>
            <p className={estilos.detalleTitulo}>Pagos (World Office y app)</p>
            <PagosDeCuenta ordenServicioId={c.orden_servicio_id} />
          </section>
        </div>
      )}

      {viendo && (
        <HojaDeLaFactura fev={viendo} ordenNumero={c.numero} onCerrar={() => setViendo(null)} />
      )}
    </article>
  )
}
